import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Award, Users, Heart, Loader2 } from 'lucide-react';
import { stylistProfileAPI } from '../services/api';

const About = () => {
  const navigate = useNavigate();
  const [stylist, setStylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await stylistProfileAPI.getProfile();
        setStylist(data);
      } catch (err) {
        console.error('Error fetching stylist profile:', err);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh'
      }}>
        <Loader2 
          size={40} 
          style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} 
        />
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error || !stylist) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        fontFamily: 'var(--font-body)',
        color: 'var(--mid-gray)'
      }}>
        {error || 'Данные не найдены'}
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, rgba(212, 196, 176, 0.15) 0%, rgba(250, 249, 247, 1) 100%)`,
        padding: 'var(--space-xxxl) var(--space-l)'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--space-xxl)',
            alignItems: 'center'
          }}>
            <div style={{ order: 2 }} className="hero-image-order">
              <div style={{
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                height: '600px',
                boxShadow: 'var(--shadow-medium)'
              }}>
                <img
                  src={stylist.image_url}
                  alt={stylist.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            </div>
            <div style={{ order: 1 }}>
              <p className="caption" style={{
                color: 'var(--warm-sage)',
                marginBottom: 'var(--space-m)'
              }}>
                О стилисте
              </p>
              <h1 style={{ marginBottom: 'var(--space-l)' }}>
                {stylist.name}
              </h1>
              <p style={{
                fontSize: '1.125rem',
                color: 'var(--warm-gray)',
                marginBottom: 'var(--space-l)',
                lineHeight: '1.7'
              }}>
                {stylist.description}
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 'var(--space-l)',
                marginTop: 'var(--space-xl)'
              }}>
                <div style={{
                  padding: 'var(--space-l)',
                  backgroundColor: 'rgba(196, 181, 160, 0.1)',
                  borderRadius: 'var(--radius-sm)'
                }}>
                  <p className="caption" style={{
                    color: 'var(--warm-sage)',
                    marginBottom: 'var(--space-s)'
                  }}>
                    Опыт работы
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-primary)',
                    fontSize: '2rem',
                    color: 'var(--charcoal)'
                  }}>
                    {stylist.experience}
                  </p>
                </div>
                <div style={{
                  padding: 'var(--space-l)',
                  backgroundColor: 'rgba(196, 181, 160, 0.1)',
                  borderRadius: 'var(--radius-sm)'
                }}>
                  <p className="caption" style={{
                    color: 'var(--warm-sage)',
                    marginBottom: 'var(--space-s)'
                  }}>
                    Город
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-primary)',
                    fontSize: '1.5rem',
                    color: 'var(--charcoal)'
                  }}>
                    {stylist.city}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-xxl)' }}>
            <h2 style={{ marginBottom: 'var(--space-m)' }}>Мой подход</h2>
            <p style={{
              color: 'var(--warm-gray)',
              maxWidth: '700px',
              margin: '0 auto',
              fontSize: '1.0625rem',
              lineHeight: '1.6'
            }}>
              Я не следую трендам слепо — я помогаю найти то, что работает именно для вас
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-xl)',
            maxWidth: '900px',
            margin: '0 auto'
          }}
          className="approach-grid">
            {stylist.approach?.map((item, index) => {
              const icons = [Users, Heart, Award, Check];
              const IconComponent = icons[index] || Check;
              return (
                <div key={index} className="card" style={{
                  textAlign: 'center',
                  padding: 'var(--space-xl)'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    backgroundColor: 'var(--warm-sage)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto var(--space-l)',
                    color: 'var(--cream-white)'
                  }}>
                    <IconComponent size={28} />
                  </div>
                  <p style={{
                    color: 'var(--charcoal)',
                    fontSize: '1.0625rem',
                    lineHeight: '1.6',
                    fontWeight: 500
                  }}>
                    {item}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section section-bg-gray">
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <h2 style={{
              marginBottom: 'var(--space-xxl)',
              textAlign: 'center'
            }}>
              Философия работы
            </h2>
            <div style={{
              display: 'grid',
              gap: 'var(--space-xl)'
            }}>
              <div className="card" style={{
                padding: 'var(--space-xl)',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 'var(--space-l)',
                alignItems: 'start'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--warm-sage)',
                  color: 'var(--cream-white)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-primary)',
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}>
                  1
                </div>
                <div>
                  <h3 style={{
                    marginBottom: 'var(--space-m)',
                    fontSize: '1.25rem'
                  }}>
                    Работа с имеющимся
                  </h3>
                  <p style={{
                    color: 'var(--warm-gray)',
                    fontSize: '1.0625rem',
                    lineHeight: '1.6'
                  }}>
                    Начинаем с того, что у вас уже есть. Часто оказывается, что гардероб полон отличных вещей — просто нужно научиться их сочетать. Докупаем только то, что действительно необходимо.
                  </p>
                </div>
              </div>

              <div className="card" style={{
                padding: 'var(--space-xl)',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 'var(--space-l)',
                alignItems: 'start'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--warm-sage)',
                  color: 'var(--cream-white)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-primary)',
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}>
                  2
                </div>
                <div>
                  <h3 style={{
                    marginBottom: 'var(--space-m)',
                    fontSize: '1.25rem'
                  }}>
                    Практичность и реальная жизнь
                  </h3>
                  <p style={{
                    color: 'var(--warm-gray)',
                    fontSize: '1.0625rem',
                    lineHeight: '1.6'
                  }}>
                    Создаю образы, которые подходят для вашей реальной жизни — работы, встреч, событий. Никаких подиумных луков, которые некуда надеть. Только то, что вы будете носить с удовольствием.
                  </p>
                </div>
              </div>

              <div className="card" style={{
                padding: 'var(--space-xl)',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 'var(--space-l)',
                alignItems: 'start'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--warm-sage)',
                  color: 'var(--cream-white)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-primary)',
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}>
                  3
                </div>
                <div>
                  <h3 style={{
                    marginBottom: 'var(--space-m)',
                    fontSize: '1.25rem'
                  }}>
                    Честная обратная связь
                  </h3>
                  <p style={{
                    color: 'var(--warm-gray)',
                    fontSize: '1.0625rem',
                    lineHeight: '1.6'
                  }}>
                    Говорю прямо, что работает, а что нет. Не буду уговаривать купить вещь, которая вам не подходит. Моя задача — ваша уверенность и комфорт, а не продажи.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="section">
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: 'var(--space-l)' }}>
              Присоединяйтесь к сообществу
            </h2>
            <p style={{
              fontSize: '1.0625rem',
              color: 'var(--warm-gray)',
              marginBottom: 'var(--space-xl)',
              lineHeight: '1.6'
            }}>
              В моем Telegram-канале я делюсь разборами образов, советами по стилю и вдохновляющими примерами. Подписывайтесь, чтобы быть в курсе.
            </p>
            <a
              href={`https://t.me/${stylist.telegram_channel?.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <button className="btn-secondary" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-m)'
              }}>
                <span>Подписаться на канал</span>
                <span style={{
                  padding: 'var(--space-xs) var(--space-m)',
                  backgroundColor: 'var(--warm-sage)',
                  color: 'var(--cream-white)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.75rem'
                }}>
                  {stylist.telegram_channel}
                </span>
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-bg-gray">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: 'var(--space-l)' }}>
            Готовы начать работу?
          </h2>
          <p style={{
            fontSize: '1.0625rem',
            color: 'var(--warm-gray)',
            maxWidth: '600px',
            margin: '0 auto var(--space-xl)',
            lineHeight: '1.6'
          }}>
            Оставьте заявку, и мы обсудим, как я могу помочь вам найти свой стиль и чувствовать себя уверенно.
          </p>
          <button
            className="btn-primary"
            onClick={() => navigate({ pathname: '/', hash: '#contact' })}
          >
            Оставить заявку
          </button>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .hero-image-order {
            order: 1;
          }
          .approach-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default About;
